import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../packages/auth-client';
import { apiClient, getErrorMessage } from '../../packages/api-client';
import { PageHeader, StateView } from '../../packages/ui';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useIsWorkspaceAdmin } from './SettingsHome';
import './Settings.css';

/** The GET /tenants/me fields this page shows (tenants.Tenant). */
interface Workspace { name: string; slug: string; created_at: string }

const CRUMBS = [{ label: 'Settings', to: '/settings' }, { label: 'Workspace' }];

/**
 * /settings/workspace — the viewer's own workspace, read-only.
 * Renaming goes through support: PUT /tenants/{id} is superadmin-only, and
 * there is no tenant-admin route to close a workspace or transfer ownership.
 */
export function WorkspaceSettings() {
    usePageTitle('Workspace settings');
    const { user } = useAuth();
    const admin = useIsWorkspaceAdmin();
    // Same key as the topbar's workspace name, so the two share one fetch.
    const q = useQuery({
        queryKey: ['tenants', 'me', user?.tenant_id],
        queryFn: async () => (await apiClient.get<Workspace>('/tenants/me')).data,
        enabled: admin && !!user?.tenant_id,
    });

    if (!admin) {
        return (
            <div className="settings-page">
                <PageHeader breadcrumb={CRUMBS} title="Workspace settings" />
                <StateView
                    state="denied"
                    reason="Workspace settings are for workspace admins."
                    nextStep="Ask an admin in your workspace to make changes."
                    action={<Link to="/settings" className="btn btn-secondary btn-md">Back to settings</Link>}
                />
            </div>
        );
    }

    // A superadmin bootstrapped outside any workspace has no tenant_id; the
    // query never runs, so say so instead of showing a skeleton forever.
    if (!user?.tenant_id) {
        return (
            <div className="settings-page">
                <PageHeader breadcrumb={CRUMBS} title="Workspace settings" />
                <StateView
                    state="empty"
                    title="No workspace"
                    message="You're a platform operator without a tenant."
                    action={<Link to="/admin/tenants" className="btn btn-secondary btn-md">Open the platform console</Link>}
                />
            </div>
        );
    }

    return (
        <div className="settings-page">
            <PageHeader breadcrumb={CRUMBS} title="Workspace settings" subtitle="These apply to everyone in your workspace." />

            <section className="card settings-section" aria-labelledby="ws-details">
                <h2 id="ws-details" className="settings-section-title">Workspace details</h2>
                {q.isPending ? (
                    <StateView state="loading" label="Loading workspace" lines={3} />
                ) : q.isError ? (
                    <StateView state="error" message={getErrorMessage(q.error)} onRetry={() => q.refetch()} retrying={q.isFetching} />
                ) : (
                    <>
                        <dl className="settings-facts">
                            <dt>Name</dt><dd>{q.data.name}</dd>
                            <dt>Slug</dt><dd><code>{q.data.slug}</code></dd>
                            <dt>Created</dt><dd>{new Date(q.data.created_at).toLocaleDateString()}</dd>
                        </dl>
                        <p className="settings-section-intro">
                            The name and slug can&apos;t be changed in the app. To rename the workspace,{' '}
                            <Link to="/help">contact support</Link>.
                        </p>
                    </>
                )}
            </section>

            <section className="card settings-section" aria-labelledby="ws-related">
                <h2 id="ws-related" className="settings-section-title">Managed elsewhere</h2>
                <ul className="settings-links">
                    <li><Link to="/people">People & access</Link>: members, invitations and roles</li>
                    <li><Link to="/billing">Billing & usage</Link>: plan, payment method, invoices and usage</li>
                    <li><Link to="/admin/ai">AI provider</Link>: the workspace&apos;s Anthropic API key</li>
                </ul>
            </section>

            <section id="lifecycle" className="card settings-section" aria-labelledby="ws-lifecycle">
                <h2 id="ws-lifecycle" className="settings-section-title">Data & lifecycle</h2>
                <p className="settings-section-intro">
                    There is no in-app way to close this workspace or transfer its ownership.
                    If you are its only member, deleting your account also schedules the workspace
                    for deletion. To stop it during the recovery window, sign in, then choose to cancel the deletion.
                    Otherwise, contact support.
                </p>
                <ul className="settings-links">
                    <li><Link to="/profile?tab=delete">Delete your account</Link></li>
                    <li><Link to="/help">Contact support about this workspace</Link></li>
                </ul>
            </section>
        </div>
    );
}
